import React, { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const RegisterPage: React.FC = () => {
    useDocumentMeta('Create a free account | CloudPrepper', 'Account access for CloudPrepper, the CompTIA Cloud+ and AWS practice exam platform.', { path: '/register', noindex: true });
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { register, isAuthenticated, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (username.length < 3) {
            setError('Username must be at least 3 characters long');
            return;
        }

        setLoading(true);

        try {
            await register(username, email, password);
            navigate('/', { replace: true, state: { firstRun: true } });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Signed in (including right after registering): leave the form for the first-run path
    // instead of sitting on /register with no next step (QA CP-010).
    if (isAuthenticated && !authLoading) {
        return <Navigate to="/" replace state={{ firstRun: true }} />;
    }

    return (
        <div className="page-below-nav landing-rainbow flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="auth-card rounded-2xl p-8">
                    <h2 className="text-2xl font-semibold auth-title mb-6 text-center">Sign Up</h2>

                    {error && (
                        <div className="mb-4 p-3 auth-error rounded-lg">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="username" className="block text-sm font-medium auth-label mb-1">Username</label>
                            <input
                                id="username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                minLength={3}
                                maxLength={50}
                                className="w-full px-4 py-3 rounded-lg auth-input"
                                placeholder="johndoe"
                                disabled={loading}
                            />
                            <p className="mt-1 text-xs auth-muted">3-50 characters, alphanumeric</p>
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium auth-label mb-1">Email Address</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full px-4 py-3 rounded-lg auth-input"
                                placeholder="you@example.com"
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium auth-label mb-1">Password</label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                className="w-full px-4 py-3 rounded-lg auth-input"
                                placeholder="••••••••"
                                disabled={loading}
                            />
                            <p className="mt-1 text-xs auth-muted">Minimum 6 characters</p>
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium auth-label mb-1">Confirm Password</label>
                            <input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                minLength={6}
                                className="w-full px-4 py-3 rounded-lg auth-input"
                                placeholder="••••••••"
                                disabled={loading}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full auth-btn py-3 px-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating account...
                                </span>
                            ) : (
                                'Create Account'
                            )}
                        </button>
                    </form>

                    <div className="mt-6 pt-6 auth-divider">
                        <p className="text-center auth-muted">
                            Already have an account?{' '}
                            <Link to="/login" className="auth-link">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>

                <p className="text-center mt-6 text-sm auth-muted">
                    By creating an account, you agree to our terms of service
                </p>
            </div>
        </div>
    );
};

export default RegisterPage;
